import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Container,
    Typography,
    Paper,
    Box,
    Button,
    Divider,
    Card,
    CardContent,
    Breadcrumbs,
    Link,
    useTheme,
    Grid,
} from '@mui/material';
import {
    NavigateNext as NavigateNextIcon,
    ThumbUp as ThumbUpIcon,
    ThumbDown as ThumbDownIcon,
    Info as InfoIcon,
    Edit as EditIcon,
    PhotoCamera as PhotoCameraIcon,
    ContactMail as ContactMailIcon,
    Security as SecurityIcon,
} from '@mui/icons-material';
import Seo from '../../components/common/Seo';

function HowToPost() {
    const theme = useTheme();

    const goodBadExamples = [
        {
            category: 'タイトル',
            good: '渋谷駅徒歩7分、明るい個室、女性専用シェアハウス',
            bad: '部屋空いてます',
            reason: 'タイトルは検索結果で最初に目に入る部分です。立地や部屋の特徴、対象者を簡潔に伝えましょう。'
        },
        {
            category: '写真',
            good: '明るい自然光の中で撮影した清潔な部屋の写真、共用スペースの様子も複数枚',
            bad: '暗くてぼやけた写真、散らかった部屋の写真',
            reason: '写真は募集の印象を大きく左右します。明るく清潔な印象の写真を複数枚用意しましょう。'
        },
        {
            category: '説明文',
            good: '部屋の広さや設備、近隣施設、生活ルールなどを具体的に記載',
            bad: '「良い物件です」「お問い合わせください」などの抽象的な表現',
            reason: '具体的な情報は相手に安心感を与え、ミスマッチを防ぎます。'
        },
        {
            category: '条件・ルール',
            good: '「禁煙」「深夜の音出し禁止」など明確なルール',
            bad: '「常識的な人」「迷惑をかけない人」などの曖昧な表現',
            reason: '明確な条件を示すことで、お互いにストレスなく生活するための基盤ができます。'
        },
    ];

    const tips = [
        {
            title: '魅力的なタイトルをつける',
            description: '物件の最大の特徴（立地、広さ、雰囲気など）を簡潔に伝えるタイトルをつけましょう。',
            icon: <EditIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />,
            examples: [
                '渋谷駅徒歩7分、日当たり良好な個室、女性向け',
                '新宿エリア、リビング広々シェアハウス、社会人向け',
                '池袋駅近！リノベーション済みデザイナーズ物件、駐輪場あり'
            ]
        },
        {
            title: '魅力的な写真を用意する',
            description: '明るく清潔な印象の写真を複数用意しましょう。部屋だけでなく、共用スペースや周辺環境も紹介すると良いでしょう。',
            icon: <PhotoCameraIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />,
            points: [
                'できるだけ自然光がある時間帯に撮影する',
                '部屋を片付けてから撮影する',
                '個室だけでなく、キッチンやリビングなどの共用スペースも撮影する',
                '角度を変えて撮影し、部屋の広さが伝わるようにする'
            ]
        },
        {
            title: '具体的な情報を提供する',
            description: '住環境や生活ルールについて、できるだけ具体的に記載しましょう。曖昧な表現は避け、明確な情報を提供することが大切です。',
            icon: <InfoIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />,
            sections: [
                {
                    title: '物件情報',
                    items: ['広さ（〇㎡）', '間取り', '築年数', '設備（エアコン、ベッド、収納など）', '共用設備（洗濯機、冷蔵庫など）']
                },
                {
                    title: '立地・アクセス',
                    items: ['最寄り駅からの距離（徒歩〇分）', '周辺施設（スーパー、コンビニなど）', '周辺環境（静かな住宅街、商店街近くなど）']
                },
                {
                    title: '費用',
                    items: ['家賃', '光熱費の負担方法', '敷金・礼金', 'その他費用（インターネット代など）']
                }
            ]
        },
        {
            title: '生活ルールを明確にする',
            description: '共同生活のルールを明確にすることで、お互いに快適に過ごすための基盤ができます。',
            icon: <SecurityIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />,
            rules: [
                '喫煙の可否',
                '来客・宿泊の可否',
                '帰宅時間（深夜の出入りに関するルールなど）',
                '共用スペースの利用ルール',
                '掃除当番や光熱費の分担方法',
                'ペットの可否'
            ]
        },
        {
            title: '連絡方法を明確にする',
            description: '問い合わせへの対応方法を明確にし、スムーズなコミュニケーションを心がけましょう。',
            icon: <ContactMailIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />,
            points: [
                '連絡可能な時間帯を記載する',
                '質問には丁寧かつ迅速に返信する',
                '必要に応じて実際の物件見学の機会を設ける',
                '個人情報のやり取りには十分注意する'
            ]
        }
    ];

    return (
        <>
            <Seo
                title="募集の書き方のコツ - ルームシェアガイド"
                description="ルームシェアやルームメイトの募集を成功させるためのコツを紹介。魅力的なタイトルの付け方、写真の撮り方、説明文の書き方など、応募が集まる投稿のポイントを解説します。"
                path="/guide/how-to-post"
                keywords="ルームシェア募集,ルームメイト募集,募集の書き方,良い写真,魅力的な説明文"
            />
            <Container maxWidth="md">
                <Paper sx={{ p: { xs: 2, md: 4 }, mt: 4, mb: 4, borderRadius: 2 }}>
                    {/* パンくずリスト */}
                    <Breadcrumbs
                        separator={<NavigateNextIcon fontSize="small" />}
                        aria-label="breadcrumb"
                        sx={{ mb: 3 }}
                    >
                        <Link component={RouterLink} to="/" color="inherit">
                            トップ
                        </Link>
                        <Link component={RouterLink} to="/guide" color="inherit">
                            ガイド
                        </Link>
                        <Typography color="text.primary">募集の書き方のコツ</Typography>
                    </Breadcrumbs>

                    <Typography
                        variant="h4"
                        component="h1"
                        gutterBottom
                        sx={{
                            fontWeight: 'bold',
                            fontSize: { xs: '1.8rem', md: '2.5rem' }
                        }}
                    >
                        募集の書き方のコツ
                    </Typography>

                    <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
                        魅力的な募集文を作成することで、理想的なルームメイトとの出会いの可能性が高まります。
                        この記事では、応募が集まりやすい募集の書き方のポイントをご紹介します。
                    </Typography>

                    <Divider sx={{ mb: 5 }} />

                    {/* Good & Bad Examples */}
                    <Typography variant="h5" component="h2" sx={{ mb: 3, fontWeight: 'bold' }}>
                        成功する募集と失敗する募集の例
                    </Typography>

                    <Grid container spacing={3} sx={{ mb: 5 }}>
                        {goodBadExamples.map((example, index) => (
                            <Grid item xs={12} key={index}>
                                <Card sx={{ mb: 3, borderRadius: 2 }}>
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                                            {example.category}
                                        </Typography>

                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                                <ThumbUpIcon sx={{ color: 'success.main' }} />
                                                <Box>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                                                        Good
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        {example.good}
                                                    </Typography>
                                                </Box>
                                            </Box>

                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                                <ThumbDownIcon sx={{ color: 'error.main' }} />
                                                <Box>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                                                        Bad
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        {example.bad}
                                                    </Typography>
                                                </Box>
                                            </Box>

                                            <Box sx={{ bgcolor: '#F5F5F5', p: 2, borderRadius: 1 }}>
                                                <Typography variant="body2">
                                                    <strong>理由：</strong> {example.reason}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>

                    {/* Detailed Tips */}
                    <Typography variant="h5" component="h2" sx={{ mb: 3, fontWeight: 'bold' }}>
                        募集を成功させるためのポイント
                    </Typography>

                    {tips.map((tip, index) => (
                        <Box key={index} sx={{ mb: 5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Box
                                    sx={{
                                        bgcolor: 'primary.light',
                                        color: 'primary.contrastText',
                                        width: 40,
                                        height: 40,
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mr: 2,
                                    }}
                                >
                                    {tip.icon}
                                </Box>
                                <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold' }}>
                                    {tip.title}
                                </Typography>
                            </Box>

                            <Typography variant="body1" sx={{ mb: 3, pl: 7 }}>
                                {tip.description}
                            </Typography>

                            {tip.examples && (
                                <Box sx={{ pl: 7, mb: 3 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                        例：
                                    </Typography>
                                    <Box sx={{ bgcolor: '#F5F5F5', p: 2, borderRadius: 1 }}>
                                        <ul style={{ margin: 0, paddingLeft: 20 }}>
                                            {tip.examples.map((example, i) => (
                                                <li key={i}>
                                                    <Typography variant="body2">
                                                        {example}
                                                    </Typography>
                                                </li>
                                            ))}
                                        </ul>
                                    </Box>
                                </Box>
                            )}

                            {tip.points && (
                                <Box sx={{ pl: 7 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                        ポイント：
                                    </Typography>
                                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                                        {tip.points.map((point, i) => (
                                            <li key={i}>
                                                <Typography variant="body2" paragraph>
                                                    {point}
                                                </Typography>
                                            </li>
                                        ))}
                                    </ul>
                                </Box>
                            )}

                            {tip.sections && (
                                <Box sx={{ pl: 7 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                        記載すべき情報：
                                    </Typography>
                                    {tip.sections.map((section, i) => (
                                        <Box key={i} sx={{ mb: 2 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                ■ {section.title}
                                            </Typography>
                                            <ul style={{ margin: 0, paddingLeft: 20 }}>
                                                {section.items.map((item, j) => (
                                                    <li key={j}>
                                                        <Typography variant="body2">
                                                            {item}
                                                        </Typography>
                                                    </li>
                                                ))}
                                            </ul>
                                        </Box>
                                    ))}
                                </Box>
                            )}

                            {tip.rules && (
                                <Box sx={{ pl: 7 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                        明確にすべきルール例：
                                    </Typography>
                                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                                        {tip.rules.map((rule, i) => (
                                            <li key={i}>
                                                <Typography variant="body2" paragraph>
                                                    {rule}
                                                </Typography>
                                            </li>
                                        ))}
                                    </ul>
                                </Box>
                            )}

                            {index < tips.length - 1 && (
                                <Divider sx={{ mt: 4 }} />
                            )}
                        </Box>
                    ))}

                    {/* Call to Action */}
                    <Box sx={{ mt: 6, p: 3, bgcolor: '#E3F2FD', borderRadius: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            実際に募集を作成してみましょう
                        </Typography>
                        <Typography variant="body2" paragraph>
                            このガイドを参考に、魅力的な募集を作成して理想のルームメイトを見つけましょう。
                            募集を公開する前に、第三者に読んでもらうと、より良い募集文になるかもしれません。
                        </Typography>
                        <Button
                            component={RouterLink}
                            to="/guide/safe-roomshare"
                            color="primary"
                            variant="contained"
                            endIcon={<NavigateNextIcon />}
                            sx={{ mt: 1 }}
                        >
                            安全なルームシェアのために
                        </Button>
                    </Box>
                </Paper>
            </Container>
        </>
    );
}

export default HowToPost; 